<template>
  <div :class="$style.itemTable">
    <ItemTable
      :columns="$options.columns"
      :details="true"
      :handleBulkDelete="handleDeleteSelectedApps"
      :handleMainAction="handleAddNewApp"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :itemType="$options.itemType"
      :keepFresh="false"
      :selectionItemText="$options.selectedLabels"
      :showBound="false"
      :showBulkDelete="true"
      :showMainAction="true"
      :showSearchForm="false"
      :showSelectedItemsCount="false"
    >
      <template v-slot:customBar>
        <div>
          <label :class="$style.itemCountLabel">
            {{ trustedAppsCountText }}
          </label>
          <Button
            :class="$style.resetButton"
            text="Restore Defaults"
            @click="handleResetToDefaultApps"
          />
        </div>
      </template>
    </ItemTable>
  </div>
</template>

<script>
import { Button } from '@jumpcloud/ui-components';
import { ItemTypeConsts, ItemTypeRoutes } from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import AuthNPoliciesCertificateSettingsMixin from 'client/Security/mixins/AuthNPoliciesCertificateSettingsMixin';
import ItemTable from 'client/components/ItemTable/ItemTable';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PageNavigationUtil from 'client/util/PageNavigationUtil';

const appAuthorizationRoute = ItemTypeRoutes[ItemTypeConsts.appAuthorization];
const appAuthorizationCollection = 'AppAuthorizationCollection';
const orgModel = 'OrganizationModel';

export default {
  name: 'AppAuthorizationItemTable',

  components: { ItemTable, Button },

  itemType: ItemTypeConsts.appAuthorization,
  AppAuthorizationAddRoute: `${appAuthorizationRoute}/new`,
  columns: [
    {
      dataField: 'name',
      headingText: 'Name',
      sortBy: 'name',
    },
    {
      dataField: 'source',
      headingText: 'Source',
      sortBy: 'source',
    },
  ],

  mixins: [AuthNPoliciesCertificateSettingsMixin],

  computed: {
    ...mapGetters(appAuthorizationCollection, ['getSelectedItems', 'getTotalCount']),

    trustedAppsCountText() {
      const selectedLabels = {
        label: 'selected',
        singular: 'trusted app',
        plural: 'trusted apps',
      };
      const trustedAppNoun = this.getTotalCount > 1
        ? selectedLabels.plural : selectedLabels.singular;
      const itemsSelected = this.getSelectedItems.length;

      switch (itemsSelected) {
        case 0:
          return `${this.getTotalCount} ${trustedAppNoun}`;
        default:
          return `${itemsSelected} of ${this.getTotalCount} ${trustedAppNoun} ${selectedLabels.label}`;
      }
    },
  },

  async mounted() {
    await this.initializeTrustedAppsSettings();
  },

  methods: {
    ...mapActions(appAuthorizationCollection, [
      'deleteItems',
      'setCollectionToRecommendedApps',
    ]),

    ...mapActions(orgModel, ['updateTrustedAppsSettings']),

    handleAddNewApp() {
      PageNavigationUtil.setLocation(this.$options.AppAuthorizationAddRoute);
    },

    async handleConfirmDelete() {
      const { deleteItems, getSelectedItems } = this;

      const trustedApps = await deleteItems(getSelectedItems);
      this.updateTrustedAppsSettings(trustedApps);
      ModalEventBus.close();
    },

    async handleConfirmReset() {
      const trustedApps = await this.setCollectionToRecommendedApps();
      this.updateTrustedAppsSettings(trustedApps);
      ModalEventBus.close();
    },

    handleDeleteSelectedApps() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          bodyText: 'Are you sure you want to forget these applications as trusted? This action can’t be undone.',
          headerText: 'Remove MacOS Keychain Application Access',
          continueText: 'delete',
          mainActionAttention: true,
          handleContinue: this.handleConfirmDelete,
        },
      });
    },

    handleResetToDefaultApps() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          bodyText: 'Restore the macOS Keychain Application Access to JumpCloud’s default configuration. This will remove any custom trusted application configurations.',
          headerText: 'Restore default configuration',
          continueText: 'Restore',
          handleContinue: this.handleConfirmReset,
        },
      });
    },

    handleRowClick(itemId) {
      PageNavigationUtil.setLocation(`${appAuthorizationRoute}/${itemId}/edit`);
    },
  },
};
</script>

<style module>

.itemTable {
  background-color: var(--jcWhite);
  height: 300px;
}

.itemCountLabel {
  font-size: 1rem;
  font-weight: 600;
}

.resetButton {
  --jc-body: 13px;
  --jc-button-padding: 5px;
}

</style>
